import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { DialogContent, DialogContentText, DialogActions } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import * as Modal from '_components/modal/generic-modal'

import Button from '_components/button'
import { unrefuseBudget } from '_modules/budget/actions'
import { currentServiceOrderSelector } from '_modules/service-orders/selectors'
import useIsCurrentStepStatusCanceled from '_hooks/use-is-current-step-status-canceled'
import { useToast } from '_/hooks/use-toast'

import useStyles from './styles'
import { getHistoryLogs } from '_/modules/history-logs/actions'
import { getServiceOrder } from '_/modules/service-orders/actions'
// import { useDialog } from '_/hooks/use-dialog'

const DIALOG_STATES = {
  DEFAULT: 'default',
  CONFIRM: 'confirm',
  HOLD: 'hold',
}

const UnrefuseBudgetDialog = ({
  isOpen,
  onClose,
  serviceOrderId,
  budgetId,
  hasAnotherAvailableBudget,
}) => {
  const styles = useStyles()
  const dispatch = useDispatch()
  const { showToast } = useToast()
  const [isLoading, setIsLoading] = useState(false)

  const refetchServiceOrder = useCallback(() => {
    dispatch(getServiceOrder(serviceOrderId))
  }, [dispatch, serviceOrderId])

  const serviceOrder = useSelector(currentServiceOrderSelector)

  const isActive = useMemo(() => Boolean(serviceOrder?.isActive), [serviceOrder?.isActive])
  const isCanceled = useIsCurrentStepStatusCanceled()

  const [dialogState, setState] = useState(
    isActive && !isCanceled ? DIALOG_STATES.CONFIRM : DIALOG_STATES.DEFAULT
  )
  const onConfirmClick = useCallback(() => {
    if (hasAnotherAvailableBudget) {
      setState(DIALOG_STATES.HOLD)
      return
    }

    setState(DIALOG_STATES.CONFIRM)
  }, [hasAnotherAvailableBudget])

  const onMarkBudgetAsRevised = useCallback(() => {
    setIsLoading(true)
    dispatch(unrefuseBudget({ serviceOrderId, budgetId }))
      .then(() => {
        refetchServiceOrder()
        dispatch(getHistoryLogs(serviceOrderId))
        onClose()
      })
      .catch(err => {
        showToast({ type: 'error', message: err?.error_message })
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [budgetId, dispatch, onClose, refetchServiceOrder, serviceOrderId, showToast])

  const renderDialogContent = useMemo(() => {
    switch (dialogState) {
      case DIALOG_STATES.CONFIRM:
        return (
          <>
            <Modal.Content>
              <Modal.InfoIcon />
              <Modal.Text id="unrefuseBudgetDialog" className={styles.description}>
                Você tem certeza que deseja cancelar a recusa deste orçamento?
              </Modal.Text>
            </Modal.Content>
            <Modal.Actions>
              <Modal.ButtonRed onClick={onClose} variant="outlined">
                Não
              </Modal.ButtonRed>
              <Modal.ButtonFullBlue
                onClick={onMarkBudgetAsRevised}
                isLoading={isLoading}
                color="primary"
                variant="contained"
              >
                Sim
              </Modal.ButtonFullBlue>
            </Modal.Actions>
          </>
        )
      case DIALOG_STATES.HOLD:
        return (
          <DialogContent>
            <DialogContentText id="unrefuseBudgetDialog" className={styles.description}>
              Este chamado possui outro orçamento aguardando aprovação ou reprovação. Reprove os
              outros orçamento ainda ativos para só depois cancelar a recusa deste orçamento.
            </DialogContentText>
          </DialogContent>
        )

      default:
        return (
          <>
            <DialogContent>
              <DialogContentText id="unrefuseBudgetDialog" className={styles.description}>
                Este chamado encontra-se cancelado. Para realizar ações em orçamentos, reative o
                chamado.
              </DialogContentText>
            </DialogContent>
            <DialogActions className={styles.button}>
              <Button onClick={onClose} variant="contained" color="primary">
                Ok
              </Button>
            </DialogActions>
          </>
        )
    }
  }, [
    dialogState,
    isLoading,
    onClose,
    onConfirmClick,
    onMarkBudgetAsRevised,
    styles.description,
    styles.button,
  ])

  return (
    <Modal.Root open={isOpen} onClose={onClose} aria-describedby="unrefuseBudgetDialog">
      {renderDialogContent}
    </Modal.Root>
  )
}

UnrefuseBudgetDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  serviceOrderId: PropTypes.string.isRequired,
  budgetId: PropTypes.number.isRequired,
  hasAnotherAvailableBudget: PropTypes.bool.isRequired,
}

export default React.memo(UnrefuseBudgetDialog)
